import { useEffect, useState } from 'react';
import { fetchAdvisorConnections, fetchLocalModelAdvice, localModelAssistantTurn, prepareLocalModelPlan } from '../../api/localModelAdvisor';
import type { Advice, AdviceItem, AdvisorConnection, AdvisorPrefill, AssistantPlan, ModelConnection } from '../../api/localModelAdvisor';
import { localAiBrowseUrl } from '../../api/localAi';

export function LocalModelAdvisor({ deviceId, onPlan }: { deviceId: string | null; onPlan: (items: AdvisorPrefill[]) => void }) {
  const [advice, setAdvice] = useState<Advice | null>(null);
  const [assistant, setAssistant] = useState<AssistantPlan | null>(null);
  const [showAssistant, setShowAssistant] = useState(false);
  const [message, setMessage] = useState('');
  const [connection, setConnection] = useState<ModelConnection>({ provider: '', model: '', vault_key_id: '' });
  const [connections, setConnections] = useState<AdvisorConnection[]>([]);
  const [connectionNote, setConnectionNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    setAdvice(null); setAssistant(null); setError('');
    if (deviceId) void fetchLocalModelAdvice(deviceId).then(data => { if (active) setAdvice(data); })
      .catch(() => { if (active) setError('Model advice could not be loaded.'); });
    return () => { active = false; };
  }, [deviceId]);
  useEffect(() => {
    if (!showAssistant) return;
    let active = true;
    void fetchAdvisorConnections().then(rows => {
      if (active) { setConnections(rows); setConnectionNote(rows.length ? '' : 'Add a provider connection in Settings to use the assistant.'); }
    }).catch(() => { if (active) setConnectionNote('Saved connections could not be loaded.'); });
    return () => { active = false; };
  }, [showAssistant]);
  async function review(items: AdviceItem[]) {
    if (!deviceId) return;
    setBusy(true); setError('');
    try { onPlan(await prepareLocalModelPlan(deviceId, items)); }
    catch { setError('The selection has changed. Refresh your device facts and try again.'); }
    finally { setBusy(false); }
  }
  return <section aria-label="Local model advisor">
    <h5>Choose models for this computer</h5>
    {!deviceId ? <p>Pair a device to check model fit.</p> : null}
    {error ? <p role="alert">{error}</p> : null}
    {advice ? <>
      <p>Ranked by: {advice.ranked_by}</p><p>{advice.note}</p>
      <ol aria-label="Guided recommendations">{advice.items.map(item => <li key={item.key}>
        <strong>{/[\\/]/.test(item.key) ? 'Model option' : item.key}</strong>: {item.explanation}
      </li>)}</ol>
      <table><caption>Model fit and unknown facts</caption><thead><tr><th>Model</th><th>Fit</th><th>Reasons</th><th>Review</th></tr></thead>
        <tbody>{advice.table.map(row => {
          const safe = localAiBrowseUrl(row.browse_url);
          return <tr key={row.key}><td>{/[\\/]/.test(row.key) ? 'Model option' : row.key}</td><td>{row.fit}</td>
            <td>{row.reasons.map(reason => reason.startsWith('fact_unknown:') ? `Unknown: ${reason.slice(13)}` : reason).join(', ')}</td>
            <td>{row.fit === 'installable' && row.runtime && row.store_label ?
              <button type="button" disabled={busy} onClick={() => void review([{ key: row.key, target_device: deviceId!,
                target_runtime: row.runtime!, target_store_label: row.store_label! }])}>Review in Local AI</button> :
              safe ? <a href={safe} target="_blank" rel="noopener noreferrer">Browse Hugging Face</a> : null}</td></tr>;
        })}</tbody></table>
    </> : null}
    <button type="button" onClick={() => setShowAssistant(!showAssistant)}>Use assistant</button>
    {showAssistant ? <form onSubmit={event => { event.preventDefault(); if (!deviceId) return;
      setBusy(true); setError('');
      void localModelAssistantTurn(deviceId, message, Object.values(connection).every(Boolean) ? connection : undefined)
        .then(setAssistant).catch(() => setError('The connected model is unavailable.')).finally(() => setBusy(false));
    }}>
      <label>Ask about models<textarea value={message} onChange={event => setMessage(event.target.value)} maxLength={4096} /></label>
      <label>Saved connection<select value={connection.vault_key_id} onChange={event => {
        const selected = connections.find(row => row.key_id === event.target.value);
        setConnection({ ...connection, provider: selected?.provider ?? '', vault_key_id: selected?.key_id ?? '' });
      }}><option value="">Choose a saved connection</option>
        {connections.map(row => <option key={row.key_id} value={row.key_id}>{row.provider} · {row.label}</option>)}
      </select></label>
      {connectionNote ? <p role="status">{connectionNote}</p> : null}
      <label>Connected model<input value={connection.model} onChange={e => setConnection({ ...connection, model: e.target.value })} /></label>
      <button type="submit" disabled={!deviceId || busy || !message.trim() || !connection.vault_key_id || !connection.model.trim()}>Ask connected model</button>
    </form> : null}
    {assistant ? <><p role="status">{assistant.status}</p><p>{assistant.message}</p><p>{assistant.note}</p>
      {assistant.items.length ? <button type="button" disabled={busy} onClick={() => void review(assistant.items)}>Review assistant plan in Local AI</button> : null}</> : null}
  </section>;
}
